import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Collapse } from '@mui/material'
import { styled } from '@mui/material/styles'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const FilterTitle = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '1.125rem',
  fontFamily: 'Prelo-Bold, sans-serif',
}))

const ClearFilters = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontSize: '1.125rem',
  fontFamily: 'Prelo-Medium, sans-serif',
  textDecoration: 'underline',
}))

interface UserManagementFilterCategoryContainerProps {
  filterCategory: string
  filterTitleKey?: string
  clearCategoryFilter: (category: string) => void
  children: React.ReactNode
  unmountOnExit?: boolean
}

/**
 * UserManagementFilterCategoryContainer provides a collapsible container for filter categories
 * in the User Management interface. It wraps filter groups with expand/collapse functionality
 * and includes a clear button for each category.
 *
 * Features:
 * - Collapsible content using Material-UI Collapse component
 * - Expand/collapse toggle with arrow icons
 * - Clear button for resetting category filters
 * - Accessibility support with ARIA attributes
 * - Internationalization support via react-i18next
 * - Test IDs for automated testing
 *
 * @param filterCategory - The category name for the filter group
 * @param filterTitleKey - Optional custom translation key for the title
 * @param clearCategoryFilter - Callback function to clear filters for this category
 * @param children - React nodes representing the filter controls
 * @param unmountOnExit - Whether to unmount the collapsed content (default: true)
 */
const UserManagementFilterCategoryContainer = ({
  filterCategory,
  filterTitleKey,
  clearCategoryFilter,
  children,
  unmountOnExit = true,
}: UserManagementFilterCategoryContainerProps) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(true)

  const handleToggleExpanded = () => {
    setExpanded(!expanded)
  }

  const handleClearCategory = () => {
    clearCategoryFilter(filterCategory)
  }

  return (
    <div className="filtersGroup">
      <div className="filtersHeader">
        <FilterTitle
          id={`clear_${filterCategory}`}
          onClick={handleToggleExpanded}
          disableRipple
          aria-expanded={expanded}
          aria-label={t('UserManagement.ToggleFilterCategory', {
            category: filterCategory,
          })}
          data-testid={`Btn_UserManagement_Toggle${filterCategory}`}>
          {filterTitleKey
            ? t(filterTitleKey)
            : t(`UserManagement.${filterCategory}`)}
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </FilterTitle>
        <ClearFilters
          onClick={handleClearCategory}
          disableRipple
          aria-describedby={`clear_${filterCategory}`}
          aria-label={t('UserManagement.ClearCategoryFilters', {
            category: filterCategory,
          })}
          data-testid={`Btn_UserManagement_Clear${filterCategory}`}>
          {t('UserManagement.Clear')}
        </ClearFilters>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit={unmountOnExit}>
        {children}
      </Collapse>
    </div>
  )
}

export default UserManagementFilterCategoryContainer
